import { useCallback, useRef, useState } from "react"

import { cn } from "@/lib/utils"

interface SignatureOverlayProps {
  signatureSrc: string
  containerWidth: number
  containerHeight: number
  x: number
  y: number
  width: number
  height: number
  onChange: (position: {
    x: number
    y: number
    width: number
    height: number
  }) => void
  onRemove: () => void
}

const MIN_SIZE = 0.03
const MAX_SIZE = 0.5

export function SignatureOverlay({
  signatureSrc,
  containerWidth,
  containerHeight,
  x,
  y,
  width,
  height,
  onChange,
  onRemove,
}: SignatureOverlayProps) {
  const [dragging, setDragging] = useState(false)
  const [resizing, setResizing] = useState(false)
  const dragStart = useRef<{
    startX: number
    startY: number
    origX: number
    origY: number
  } | null>(null)
  const resizeStart = useRef<{
    startX: number
    startY: number
    origW: number
    origH: number
  } | null>(null)

  const pixelX = x * containerWidth
  const pixelY = y * containerHeight
  const pixelW = width * containerWidth
  const pixelH = height * containerHeight

  const handleDragStart = useCallback(
    (e: React.PointerEvent) => {
      if (resizing) return
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      setDragging(true)
      dragStart.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: x,
        origY: y,
      }
    },
    [x, y, resizing],
  )

  const handleDragMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging || !dragStart.current) return
      const dx = (e.clientX - dragStart.current.startX) / containerWidth
      const dy = (e.clientY - dragStart.current.startY) / containerHeight

      const newX = Math.max(
        0,
        Math.min(1 - width, dragStart.current.origX + dx),
      )
      const newY = Math.max(
        0,
        Math.min(1 - height, dragStart.current.origY + dy),
      )

      onChange({ x: newX, y: newY, width, height })
    },
    [dragging, containerWidth, containerHeight, width, height, onChange],
  )

  const handleDragEnd = useCallback(() => {
    setDragging(false)
    dragStart.current = null
  }, [])

  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()
      e.currentTarget.setPointerCapture(e.pointerId)
      setResizing(true)
      resizeStart.current = {
        startX: e.clientX,
        startY: e.clientY,
        origW: width,
        origH: height,
      }
    },
    [width, height],
  )

  const handleResizeMove = useCallback(
    (e: React.PointerEvent) => {
      if (!resizing || !resizeStart.current) return
      const dx = (e.clientX - resizeStart.current.startX) / containerWidth
      const dy = (e.clientY - resizeStart.current.startY) / containerHeight

      const newW = Math.max(
        MIN_SIZE,
        Math.min(MAX_SIZE, resizeStart.current.origW + dx),
      )
      const newH = Math.max(
        MIN_SIZE,
        Math.min(MAX_SIZE, resizeStart.current.origH + dy),
      )

      onChange({ x, y, width: newW, height: newH })
    },
    [resizing, containerWidth, containerHeight, x, y, onChange],
  )

  const handleResizeEnd = useCallback(() => {
    setResizing(false)
    resizeStart.current = null
  }, [])

  return (
    <div
      className={cn(
        "absolute cursor-grab border-2 border-dashed border-blue-500 bg-blue-500/10",
        dragging && "cursor-grabbing",
      )}
      style={{
        left: pixelX,
        top: pixelY,
        width: pixelW,
        height: pixelH,
      }}
      onPointerDown={handleDragStart}
      onPointerMove={handleDragMove}
      onPointerUp={handleDragEnd}
    >
      <img
        src={signatureSrc}
        alt="Signature"
        className="pointer-events-none h-full w-full object-contain"
        draggable={false}
      />

      <button
        className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white hover:bg-red-600"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
      >
        &times;
      </button>

      <div
        className="absolute -right-1 -bottom-1 h-3 w-3 cursor-se-resize rounded-sm bg-blue-500"
        onPointerDown={handleResizeStart}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeEnd}
      />
    </div>
  )
}
